/*eslint-disable camelcase */
import { Button } from 'primereact/button';
import { Chart } from 'primereact/chart';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Divider } from 'primereact/divider';
import { TabPanel, TabView } from 'primereact/tabview';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart as ChartJS } from 'chart.js';
import logoUrl from '../../../assets/checkmark.svg';
import { CaluculateMetricValue, metricValueFormat } from '../../../utils/metricValueFormat';
import { monthNames } from '../../../utils/typeConstants';
import { selectIsUseCoachingSkills } from '../../../pages/login/authSlice';
import '../metrics/MetricCharts.scss';
import { CoreValueDropdown } from '../../../components/coreValueDropdown/CoreValueDropdown';
import { SkillBehaviorDropdown } from '../../../components/skillBehaviorDropdown/SkillBehaviorDropdown';
import DescriptionBox from '../../../components/descriptionBox/DescriptionBox';

ChartJS.register(ChartDataLabels);

interface MetricCreationProps {
    coachingMetric: any;
    metricId?: number;
    goalMetricData?: any;
    readOnly?: boolean;
}
const MetricCreation = forwardRef<any, MetricCreationProps>((props, ref) => {
    const [basicData, setBasicData] = useState<any>();
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [label, setLabel] = useState<any>([]);
    const [metricData, setMetricData] = useState<any>([]);
    const [coachingMetricData, setCoachingMetricData] = useState<any>([]);
    const [showTLCpmment, setShowTLCpmment] = useState<boolean>(false);
    const [coreValue, setCoreValue] = useState<number>(0);
    const [currentMetricDirection, setCurrentMetricDirection] = useState<number>(1);
    const [minValue, setMinValue] = useState<number>(0);
    const [maxValue, setMaxValue] = useState<number>(100);
    const [metricValueColors, setMetricValueColors] = useState<string[]>([]);
    const isUseCoachingSkills = useSelector(selectIsUseCoachingSkills);
    const [skill, setSkill] = useState(0);
    const [behavior, setBehavior] = useState(0);
    const [metricDataLocal, setMetricDataLocal] = useState<any>([]);

    useEffect(() => {
        if (props?.goalMetricData && props.goalMetricData.length > 0) {
            setMetricDataLocal(props.goalMetricData);
        }
    }, [props?.goalMetricData]);
    useEffect(() => {
        setShowTLCpmment(false);
        if (metricDataLocal?.length > 0) {
            const currentMetricGoal = metricDataLocal[activeIndex];
            if (currentMetricGoal) {
                if (
                    currentMetricGoal?.core_value_id ||
                    currentMetricGoal?.coaching_skill_id ||
                    currentMetricGoal?.coaching_behavior_id ||
                    (currentMetricGoal?.author_comment && currentMetricGoal?.author_comment !== '')
                )
                    setShowTLCpmment(true);
            }
        }
    }, [activeIndex, metricDataLocal]);

    useEffect(() => {
        if (metricDataLocal[activeIndex] && !props?.readOnly) {
            setMetricDataLocal((prev: any) => {
                const updated = [...prev];
                updated[activeIndex] = {
                    ...updated[activeIndex],
                    core_value_id: coreValue,
                    coaching_skill_id: skill,
                    coaching_behavior_id: behavior,
                };
                return updated;
            });
        }
    }, [skill, behavior, coreValue, props?.readOnly]);

    useEffect(() => {
        if (props?.coachingMetric?.data?.length > 0) {
            const array: any = [];
            props?.coachingMetric?.data[0]?.metrics?.forEach((item: any) => {
                const obj = {
                    label: item.label,
                    metricId: item.metric_id,
                    metricDirectionId: item?.monthlyMetrics[0]?.metric_direction_id,
                };
                array.push(obj);
            });
            setLabel(array);
            setMetricData(props?.coachingMetric?.data[0]?.metrics);
            if (props?.metricId !== undefined) {
                const currentMetricData = props?.coachingMetric?.data[0]?.metrics.find(
                    (item: any) => item.metric_id === props?.metricId
                );
                setCoachingMetricData(currentMetricData?.monthlyMetrics);
                renderMetrics(currentMetricData?.monthlyMetrics);
                calculateMinMaxValues(currentMetricData?.monthlyMetrics);
                setCurrentMetricDirection(currentMetricData?.monthlyMetrics[0]?.metric_direction_id);
                setActiveIndex(
                    array.findIndex((item: any) => {
                        return item.metricId === props?.metricId;
                    })
                );
            } else {
                setCoachingMetricData(props?.coachingMetric?.data[0]?.metrics[0]?.monthlyMetrics);
                renderMetrics(props?.coachingMetric?.data[0]?.metrics[0]?.monthlyMetrics);
                calculateMinMaxValues(props?.coachingMetric?.data[0]?.metrics[0]?.monthlyMetrics);
            }
        }
    }, [props?.coachingMetric]);

    const calculateMinMaxValues = (individual: any) => {
        const metricValues = individual?.map((item: any) =>
            CaluculateMetricValue(Number(item.metric_value) || 0, item.metric_type_name)
        );
        const targetValues = individual?.map((item: any) =>
            CaluculateMetricValue(Number(item.target) || 0, item.metric_type_name)
        );

        const allValues = [...metricValues, ...targetValues];
        const minY = Math.min(...allValues, 0); // Ensure 0 is included
        const maxY = Math.max(...allValues);

        setMinValue(minY);
        setMaxValue(maxY + maxY / 8);
    };
    const basicOptions = React.useMemo(() => ({
        maintainAspectRatio: false,
        aspectRatio: 0.9,
        responsive: true,
        plugins: {
            legend: { position: 'top', display: false },
            datalabels: {
                display: true,
                anchor: 'end',
                align: 'top',
                offset: -2,
                formatter: (value: any) => {
                    return value;
                },
                font: {
                    weight: 'bold',
                },
                color: (context: any) => {
                    const idx = context.dataIndex;
                    return metricValueColors[idx];
                },
            },
        },
        scales: {
            x: {
                display: true,
                grid: {
                    color: 'transparent',
                    drawBorder: true,
                    borderWidth: 3,
                    borderColor: '#000000',
                },
                title: {
                    display: true,
                    text: 'Months',
                },
                ticks: {
                    color: '#495057',
                },
            },
            y: {
                display: true,
                grid: {
                    color: 'transparent',
                    drawBorder: true,
                    borderWidth: 3,
                    borderColor: '#000000',
                },
                title: {
                    display: true,
                    text: 'Metric Value',
                },
                ticks: {
                    beginAtZero: true,
                    color: '#495057',
                    autoskip: false, // Show all ticks
                    callback(value: any, index: number, ticks: any) {
                        const values = ticks.map((t: any) => t.value);
                        const min = Math.min(...values);
                        const max = Math.max(...values);
                        if (value === min) {
                            return ['Min.', value.toFixed(2)]; // Show min value with 2 decimal places
                        } else if (value === max) {
                            return ['Max.', value.toFixed(2)]; // Show max value with 2 decimal places
                        } else {
                            return ''; // hide other ticks
                        }
                    },
                },
                min: minValue, // Set minimum value for y-axis
                max: maxValue, // Set maximum value for y-axis (adjust as needed)
            },
        },
    }), [metricValueColors, minValue, maxValue]);
    const targetMetricValueBodyTemplate = (rowData: any, type: string) => {
        let displayimg = '--';
        let targetValue = '--';
        let metricValue = '--';
        let monthValue = '--';
        let metricValueColor = 'black';

        if (rowData && type === 'target') {
            if (rowData.target) {
                targetValue = metricValueFormat(rowData.target, rowData.metric_type_name);
            }
        } else if (rowData && type === 'metric_value') {
            if (rowData.metric_value) {
                metricValue = metricValueFormat(rowData.metric_value, rowData.metric_type_name);
                const metricValueInNumber = CaluculateMetricValue(
                    rowData.metric_value,
                    rowData.metric_type_name
                );
                const targetValueInNumber = CaluculateMetricValue(rowData.target, rowData.metric_type_name);
                if (rowData.metric_direction_id === 1) {
                    metricValueColor = metricValueInNumber >= targetValueInNumber ? '#2AA154' : '#C90370';
                } else if (rowData.metric_direction_id === 2) {
                    metricValueColor = metricValueInNumber <= targetValueInNumber ? '#2AA154' : '#C90370';
                }
            }
        } else if (rowData && type === 'label') {
            displayimg = 'metric' + rowData.metric_index;
        } else if (rowData && type === 'month') {
            monthValue = monthNames[rowData.month] + ' ' + rowData.year;
        }

        return (
            <>
                {type === 'month' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        {/* <span
                            style={{
                                height: '0.625rem',
                                width: '0.625rem',
                                borderRadius: '50%',
                            }}
                        /> */}
                        <span style={{ fontSize: '15px', fontWeight: 'bold' }} className="image-text">
                            {monthValue}
                        </span>
                    </div>
                )}
                {type === 'target' && (
                    <span className="image-text" style={{ color: '#1A5AA5', fontSize: '15px' }}>
                        {targetValue}
                    </span>
                )}
                {type === 'metric_value' && (
                    <span className="image-text" style={{ fontSize: '15px', color: metricValueColor }}>
                        {metricValue}
                    </span>
                )}
                {rowData.metric_attained && type === 'metric_attained' && (
                    <img className="justify-content-left " src={logoUrl} alt="Empower" />
                )}
                {rowData && type === 'label' && (
                    <div className="flex align-items-center gap-2">
                        <span className={displayimg} />
                        <span className="imagespacing">{rowData.label}</span>
                    </div>
                )}
            </>
        );
    };
    const chartBackgroundPlugin = React.useMemo(() => ({
        id: 'customBackgroundColor',
        beforeDraw: (chart: any) => {
            const { ctx, chartArea: area, scales } = chart;
            const threshold = chart.data.datasets[1].data[0]; // assuming threshold is consistent

            const yThreshold = scales.y.getPixelForValue(threshold);

            // Clear existing background
            ctx.save();
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(area.left, area.top, area.right - area.left, area.bottom - area.top);
            ctx.restore();

            // Draw background above threshold (e.g., light green)
            ctx.save();
            ctx.fillStyle = currentMetricDirection === 1 ? '#d5eedd' : '#efd8e1'; // green above threshold
            ctx.fillRect(area.left, area.top, area.right - area.left, yThreshold - area.top);
            ctx.restore();

            // Draw background below threshold (e.g., light red)
            ctx.save();
            ctx.fillStyle = currentMetricDirection === 1 ? '#efd8e1' : '#d5eedd'; // red below threshold
            ctx.fillRect(area.left, yThreshold, area.right - area.left, area.bottom - yThreshold);
            ctx.restore();
        },
    }), [currentMetricDirection]);
    const renderMetrics = (individual: any) => {
        const metric = individual?.map((item: any) =>
            CaluculateMetricValue(item.metric_value, item.metric_type_name)
        );
        const thresholdData = individual?.map((item: any) =>
            CaluculateMetricValue(item.target, item.metric_type_name)
        );
        const labels = individual?.map((item: any) => monthNames[item.month] + ' ' + item.year);

        const canvas = document.createElement('canvas');
        const canvasCtx = canvas.getContext('2d');
        // Default background color for chart
        const backgroundColors = individual.map((item: any) => {
            const value = CaluculateMetricValue(item.metric_value, item.metric_type_name);
            const target = CaluculateMetricValue(item.target, item.metric_type_name);
            const direction = item.metric_direction_id;

            const greenGradient = canvasCtx?.createLinearGradient(0, 0, 0, 400);

            if (greenGradient) {
                greenGradient.addColorStop(0, '#98FFAB');
                greenGradient.addColorStop(1, '#124D86');
            }
            const redGradient = canvasCtx?.createLinearGradient(0, 0, 0, 400);

            if (redGradient) {
                redGradient.addColorStop(0, '#FF98BA');
                redGradient.addColorStop(1, '#124D86');
            }
            if (direction === 1) {
                return value >= target ? greenGradient : redGradient;
            } else if (direction === 2) {
                return value <= target ? greenGradient : redGradient;
            }
            return 'transparent'; // Fallback color
        });
        const metricValueColors = individual.map((item: any) => {
            const value = CaluculateMetricValue(item.metric_value, item.metric_type_name);
            const target = CaluculateMetricValue(item.target, item.metric_type_name);
            const direction = item.metric_direction_id;
            if (item.metric_value === null || item.metric_value === undefined) return 'black'; // Default color for null values
            if (direction === 1) {
                return value >= target ? '#2AA154' : '#C90370';
            } else if (direction === 2) {
                return value <= target ? '#2AA154' : '#C90370';
            }
            return 'black'; // Fallback color
        });
        setMetricValueColors(metricValueColors);
        const data = {
            labels,
            datasets: [
                {
                    label: 'Coaching Metric',
                    data: metric,
                    backgroundColor: backgroundColors,
                    barThickness: 'flex',
                    borderRadius: { topLeft: 8, topRight: 8, bottomLeft: 0, bottomRight: 0 },
                    borderSkipped: false,
                    borderWidth: 1,
                    borderColor: 'grey',
                    order: 1,
                    datalabels: {
                        display: true,
                        font: {
                            weight: 'bold',
                        },
                        formatter: (value: any) => value,
                        anchor: 'end',
                    },
                },
                {
                    label: 'Target',
                    data: thresholdData,
                    type: 'line',
                    fill: false,
                    borderColor: 'blue',
                    borderWidth: 2,
                    borderDash: [5, 5],
                    datalabels: {
                        display: false,
                    },
                },
            ],
        };
        setBasicData(data);
    };

    const handleTabChange = (e: any) => {
        const currentIndex = e.index;
        setActiveIndex(currentIndex);
        const individual = metricData.find((item: any) => item.metric_id === label[currentIndex].metricId);
        setCoachingMetricData(individual.monthlyMetrics);
        renderMetrics(individual.monthlyMetrics);
        calculateMinMaxValues(individual.monthlyMetrics);
        setCurrentMetricDirection(individual?.monthlyMetrics[0]?.metric_direction_id);
        const currentMetricGoal = metricDataLocal[currentIndex];
        if (currentMetricGoal) {
            setSkill(currentMetricGoal?.coaching_skill_id || 0);
            setBehavior(currentMetricGoal?.coaching_behavior_id || 0);
            setCoreValue(currentMetricGoal?.core_value_id || 0);
        }
    };

    const headerBodyTemplate = (header: string) => {
        return (
            <span
                style={{ fontSize: '16px', fontWeight: 'bold', paddingLeft: header === 'Month' ? '5px' : '' }}
            >
                {header}
            </span>
        );
    };

    const handleFormChange = (event: any) => {
        if (props?.readOnly) return; // Don't allow changes in readonly mode

        const data = metricDataLocal.map((item: any, index: number) => {
            if (index === activeIndex) {
                return {
                    ...item,
                    [event.target.name]: event.target.value,
                };
            }
            return item;
        });

        setMetricDataLocal(data);
    };
    useImperativeHandle(ref, () => ({
        getLatestMetricData: () => metricDataLocal,
    }));
    return (
        <TabView
            activeIndex={activeIndex}
            onTabChange={(e) => {
                handleTabChange(e);
            }}
        >
            {label &&
                label.length > 0 &&
                label.map((item: any, i: number) => {
                    return (
                        <TabPanel key={i} header={item?.label}>
                            {item?.metricDirectionId === 2 && (
                                <div className="row">
                                    <span style={{ padding: '20px 0px 0px 100px', color: '#C90370' }}>
                                        This is a Descending Metric. Any Score above target is not desired.
                                    </span>
                                </div>
                            )}
                            <div className="row mt-4">
                                <div className="col-md-6">
                                    <div className="container">
                                        <Chart
                                            style={{
                                                height: '250px',
                                                width: '500px',
                                                backgroundColor: '#ffffff',
                                            }}
                                            type="bar"
                                            data={basicData}
                                            options={basicOptions}
                                            plugins={[chartBackgroundPlugin, ChartDataLabels]}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 ps-0 mx-auto" style={{ zIndex: '1px' }}>
                                    <div className="card border-0 metricprogresstable floatend">
                                        <DataTable
                                            style={{
                                                width: '75%',
                                            }}
                                            className="targetMetricValueTable"
                                            value={coachingMetricData}
                                        >
                                            <Column
                                                field="month"
                                                style={{ width: '50%', backgroundColor: 'transparent' }}
                                                header={headerBodyTemplate('Month')}
                                                body={(rowData) => {
                                                    return targetMetricValueBodyTemplate(rowData, 'month');
                                                }}
                                            />
                                            <Column
                                                field="target"
                                                style={{ width: '25%', backgroundColor: 'transparent' }}
                                                header={headerBodyTemplate('Target')}
                                                //className="target-metric-value-column"
                                                body={(rowData) =>
                                                    targetMetricValueBodyTemplate(rowData, 'target')
                                                }
                                            />
                                            <Column
                                                field="metric_value"
                                                style={{ width: '25%', backgroundColor: 'transparent' }}
                                                header={headerBodyTemplate('Actual')}
                                                //className="target-metric-value-column"
                                                body={(rowData) =>
                                                    targetMetricValueBodyTemplate(rowData, 'metric_value')
                                                }
                                            />
                                        </DataTable>
                                    </div>
                                </div>
                            </div>
                            {showTLCpmment || props?.readOnly ? (
                                ''
                            ) : (
                                <Button
                                    label="New Comment"
                                    onClick={() => {
                                        setShowTLCpmment(true);
                                    }}
                                    className="btn-primary p-2 mb-2 float-end"
                                />
                            )}
                            <Divider />
                            {showTLCpmment ? (
                                <div className="logConversation mt-4 mb-4">
                                    <div className="flex mb-3">
                                        {isUseCoachingSkills === true ? (
                                            <div className="col-md-10 mb-2 mt-1">
                                                <SkillBehaviorDropdown
                                                    fullWidth
                                                    skill={
                                                        metricDataLocal[activeIndex]?.coaching_skill_id || 0
                                                    }
                                                    setSkill={setSkill}
                                                    behavior={
                                                        metricDataLocal[activeIndex]?.coaching_behavior_id ||
                                                        0
                                                    }
                                                    setBehavior={setBehavior}
                                                    readOnly={props?.readOnly}
                                                    aiSkillName={
                                                        metricDataLocal[activeIndex]?.skill_name || ''
                                                    }
                                                    aiBehaviorName={
                                                        metricDataLocal[activeIndex]?.behavior_name || ''
                                                    }
                                                />
                                            </div>
                                        ) : (
                                            <div className="col-md-3 mb-2">
                                                <CoreValueDropdown
                                                    fullWidth
                                                    coreValue={coreValue}
                                                    setCoreValue={setCoreValue}
                                                    coreValueName={
                                                        metricDataLocal[activeIndex]?.core_value_name || ''
                                                    }
                                                />
                                            </div>
                                        )}
                                        <div className="me-2">
                                            <DescriptionBox
                                                title="Team Lead Comments"
                                                value={metricDataLocal[activeIndex].author_comment}
                                                responseDescription={
                                                    metricDataLocal[activeIndex].author_comment
                                                }
                                                readOnly={props?.readOnly}
                                                limit={1000}
                                                onChange={(value) => {
                                                    const syntheticEvent = {
                                                        target: { name: 'author_comment', value },
                                                    };
                                                    handleFormChange(syntheticEvent);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                ''
                            )}
                        </TabPanel>
                    );
                })}
        </TabView>
    );
});

export default MetricCreation;
